import type { FulfillmentOrderRow } from '../infrastructure/FulfillmentAdminClient';
import type { ExportOrder, ExportOrderLineItem } from '../pages/oms/orders/utils/exportOrderShape';

/**
 * Adapts a Fulfillment 2.0 list row to what the carrier exports read, so 2.0 reuses the OMS export
 * modules instead of growing its own. The JSON columns arrive untyped and shaped by ingestion, so
 * every read is defensive: a missing column yields an absent field rather than a thrown export.
 */
export const toExportOrders = (rows: FulfillmentOrderRow[]): ExportOrder[] => rows.map(toExportOrder);

/**
 * The 2.0 counterpart of the OMS Yun filter. `prepare-shipment` is a WooCommerce status OMS set when it
 * booked the parcel; 2.0 does not move the store status, so a READY order (every line in, waiting to go
 * out) on a Yun parcel is the one the carrier sheet is for.
 */
export const filterReadyYunOrders = (orders: ExportOrder[]): ExportOrder[] =>
  orders.filter((order) => order.fulfillmentStatus === 'READY' && order.shippingData?.provider === 'YUN_EXPRESS');

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const asText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toExportOrder = (row: FulfillmentOrderRow): ExportOrder => {
  const shipping = asRecord(row.shipping);
  const shippingAddress = asRecord(shipping?.['shippingAddress']);
  const billingAddress = asRecord(shipping?.['billingAddress']);
  // The API nests the customer inside its own envelope; the inner record is the person.
  const customer = asRecord(asRecord(row.customer)?.['customer']) ?? asRecord(row.customer);
  const placedAt = row.orderPlacedAt ?? '';

  return {
    orderId: String(row.orderId),
    orderDate: placedAt,
    orderDateTime: placedAt,
    // The carrier filters read the WooCommerce status; the fulfillment status speaks a different vocabulary.
    status: row.wcStatus ?? undefined,
    fulfillmentStatus: row.fulfillmentStatus,
    currency: asText(asRecord(row.amounts)?.['currency']),
    countryCode: row.countryCode ?? undefined,
    pic: row.picUser ?? undefined,
    customer: customer
      ? { email: asText(customer['email']), phone: asText(customer['phone']), name: asText(customer['name']) }
      : undefined,
    consignee: asRecord(shipping?.['consignee']) ? toConsignee(asRecord(shipping?.['consignee'])!) : undefined,
    shippingAddress: shippingAddress ? toShippingAddress(shippingAddress, billingAddress) : undefined,
    shippingData: row.shipment
      ? {
          provider: row.shipment.provider ?? undefined,
          providerOrderId: row.shipment.providerOrderId ?? undefined,
          trackingNumber: row.shipment.trackingNumber ?? undefined,
        }
      : null,
    lineItems: (row.lines ?? []).map(toLineItem),
  };
};

const toConsignee = (consignee: Record<string, unknown>): ExportOrder['consignee'] => ({
  name: asText(consignee['name']),
  address: asText(consignee['address']),
  countryCode: asText(consignee['countryCode']),
  city: asText(consignee['city']),
  state: asText(consignee['state']),
  zipcode: asText(consignee['zipcode']),
});

/** Shipping JSON often omits the email; billing carries it, and the carrier needs one to notify. */
const toShippingAddress = (
  address: Record<string, unknown>,
  billing: Record<string, unknown> | null,
): ExportOrder['shippingAddress'] => ({
  firstName: asText(address['firstName']),
  lastName: asText(address['lastName']),
  company: asText(address['company']),
  address1: asText(address['address1']),
  address2: asText(address['address2']),
  city: asText(address['city']),
  state: asText(address['state']),
  postcode: asText(address['postcode']),
  country: asText(address['country']),
  phone: asText(address['phone']),
  email: asText(address['email']) ?? asText(billing?.['email']),
});

const toLineItem = (line: NonNullable<FulfillmentOrderRow['lines']>[number]): ExportOrderLineItem => {
  const item: ExportOrderLineItem = {
    sku: line.sku,
    name: line.productName ?? undefined,
    nameKor: line.productNameKr ?? undefined,
    quantity: line.quantity ?? undefined,
    price: line.unitPrice ?? undefined,
  };
  // The tracking export reads this when the parcel has no waybill of its own.
  if (line.sourcingTrackingNumber || line.sourcingOrderNumber) {
    item.sourcingShipmentTrack = {
      trackingNumber: line.sourcingTrackingNumber ?? undefined,
      orderNumber: line.sourcingOrderNumber ?? undefined,
    };
  }
  return item;
};
